import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Frame, Loader2, Check, X, ChevronRight, 
  Sparkles, RefreshCw, Clock, Download, AlertCircle,
  Palette, Sliders, Wand2, MessageSquare
} from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Example prompts for AI custom frames
const EXAMPLE_PROMPTS = [
  "Golden roses and butterflies elegant border",
  "Neon cyberpunk frame with glowing edges",
  "Watercolor flowers in pink and purple",
  "Tropical leaves and flowers summer frame",
  "Sparkly stars and moon magical border",
  "Vintage Victorian ornate gold frame",
  "Modern geometric shapes in blue tones",
  "Christmas holly and snowflakes border"
];

export default function VideoFrameDesigner({ 
  videoUrl, 
  videoName,
  onComplete,
  onClose 
}) {
  const [categories, setCategories] = useState([]);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [borderWidth, setBorderWidth] = useState(30);
  const [customColor, setCustomColor] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [currentJob, setCurrentJob] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [step, setStep] = useState(1); // 1: Select frame, 2: Customize, 3: Processing
  
  // AI Custom Frame states
  const [useAIFrame, setUseAIFrame] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  const getAuthToken = () => localStorage.getItem('auth_token');

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [pollInterval]);

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await fetch(`${API_URL}/api/video-studio/frames/templates`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch templates');
      
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load frame templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleFrameSelect = (frame) => {
    setSelectedFrame(frame);
    // Set default border color from frame
    setCustomColor('');
  };

  const handleApplyFrame = async () => {
    if (!videoUrl) {
      toast.error('No video selected');
      return;
    }

    if (!selectedFrame && !useAIFrame) {
      toast.error('Please select a frame template or use AI custom frame');
      return;
    }
    
    if (useAIFrame && !aiPrompt.trim()) {
      toast.error('Please describe your custom frame');
      return;
    }

    try {
      setLoading(true);
      setStep(3);

      let response;
      
      if (useAIFrame) {
        // Use AI custom frame endpoint
        const requestBody = {
          video_url: videoUrl,
          prompt: aiPrompt.trim(),
          border_width: borderWidth
        };

        response = await fetch(`${API_URL}/api/video-studio/frames/generate-custom`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify(requestBody)
        });
      } else {
        // Use template frame endpoint
        const requestBody = {
          video_url: videoUrl,
          frame_id: selectedFrame.id,
          border_width: borderWidth,
          custom_color: customColor || null
        };

        response = await fetch(`${API_URL}/api/video-studio/frames/apply`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`
          },
          body: JSON.stringify(requestBody)
        });
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to apply frame');
      }

      const data = await response.json();
      setCurrentJob({ id: data.job_id, status: 'processing', progress: 0 });
      
      // Start polling for job status
      startPolling(data.job_id);
      
      toast.success(useAIFrame ? 'Generating AI custom frame...' : 'Applying frame to video...');
    } catch (error) {
      console.error('Error applying frame:', error);
      toast.error(error.message || 'Failed to apply frame');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (jobId) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/video-studio/frames/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        
        if (!response.ok) throw new Error('Failed to fetch job status');
        
        const job = await response.json();
        setCurrentJob(job);

        if (job.status === 'completed') {
          clearInterval(interval);
          setPollInterval(null);
          toast.success('Frame applied successfully!');
          if (onComplete) {
            onComplete({
              url: job.output_video_url,
              name: `Framed - ${videoName}`
            });
          }
        } else if (job.status === 'failed') {
          clearInterval(interval);
          setPollInterval(null);
          toast.error(`Failed: ${job.error || 'Unknown error'}`);
          setStep(2);
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    }, 2000);

    setPollInterval(interval);
  };

  const renderFrameGrid = () => (
    <div className="space-y-6">
      {/* AI Custom Frame Option - Featured at top */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-cyan-400" />
          <h3 className="text-sm font-semibold text-white">AI Custom Frame</h3>
          <span className="px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-full">NEW</span>
        </div>
        <button
          onClick={() => {
            setUseAIFrame(true);
            setSelectedFrame(null);
            setStep(2);
          }}
          className={`w-full p-4 rounded-xl border border-dashed transition-all ${
            useAIFrame 
              ? 'border-cyan-500 bg-cyan-500/10' 
              : 'border-zinc-600 bg-zinc-800/30 hover:border-cyan-500/50 hover:bg-zinc-800/50'
          }`}
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-white">Describe Your Perfect Frame</div>
              <div className="text-sm text-zinc-400 mt-1">
                Use AI to generate a unique custom frame from your description
              </div>
              <div className="text-xs text-cyan-400 mt-2">
                Example: "Golden roses and butterflies elegant border"
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-zinc-700" />
        <span className="text-xs text-zinc-500">or choose a template</span>
        <div className="flex-1 h-px bg-zinc-700" />
      </div>

      {/* Template frames */}
      {loadingTemplates ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
        </div>
      ) : (
        categories.map((category) => (
          <div key={category.id} className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">{category.icon}</span>
              <h3 className="text-sm font-semibold text-white">{category.name}</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {category.templates.map((frame) => {
                const isSelected = selectedFrame?.id === frame.id && !useAIFrame;
                const gradientColors = frame.gradient || [frame.border_color];
                const gradientStyle = gradientColors.length > 1 
                  ? `linear-gradient(135deg, ${gradientColors.join(', ')})`
                  : gradientColors[0];
                
                return (
                  <button
                    key={frame.id}
                    onClick={() => {
                      setUseAIFrame(false);
                      handleFrameSelect(frame);
                    }}
                    className={`relative p-3 rounded-xl border transition-all text-left ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-500/20 ring-2 ring-purple-500/50' 
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600 hover:bg-zinc-800'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <Check className="w-4 h-4 text-purple-400" />
                      </div>
                    )}
                    {/* Frame preview */}
                    <div 
                      className="w-full h-12 rounded-md mb-2 flex items-center justify-center"
                      style={{ 
                        background: gradientStyle,
                        padding: '4px'
                      }}
                    >
                      <div className="w-full h-full bg-zinc-900/80 rounded-sm" />
                    </div>
                    <div className="text-2xl mb-1">{frame.icon}</div>
                    <div className="text-sm font-medium text-white truncate">{frame.name}</div>
                    <div className="text-xs text-zinc-400 mt-1 truncate">{frame.description}</div>
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderCustomizeStep = () => (
    <div className="space-y-6">
      {/* AI Custom Frame mode */}
      {useAIFrame && (
        <div className="p-4 rounded-xl border border-cyan-500/30 bg-cyan-500/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-semibold text-white">AI Custom Frame</div>
              <div className="text-sm text-zinc-400">Describe your unique frame design</div>
            </div>
            <button 
              onClick={() => { setUseAIFrame(false); setAiPrompt(''); setStep(1); }}
              className="ml-auto p-1 text-zinc-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* AI Prompt input */}
          <div className="space-y-3">
            <Textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Describe your frame design... e.g., 'Golden roses and butterflies elegant border' or 'Neon cyberpunk frame with glowing edges'"
              className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
            />
            
            {/* Example prompts */}
            <div className="space-y-2">
              <Label className="text-xs text-zinc-500">Try these examples:</Label>
              <div className="flex flex-wrap gap-2">
                {EXAMPLE_PROMPTS.slice(0, 4).map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setAiPrompt(example)}
                    className="px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded-full text-zinc-400 hover:text-white hover:border-cyan-500/50 transition-colors"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Selected template frame display */}
      {selectedFrame && !useAIFrame && (
        <div 
          className="p-4 rounded-xl border"
          style={{ 
            borderColor: selectedFrame.border_color,
            backgroundColor: `${selectedFrame.border_color}15`
          }}
        >
          <div className="flex items-center gap-3">
            <div className="text-3xl">{selectedFrame.icon}</div>
            <div>
              <div className="font-semibold text-white">{selectedFrame.name}</div>
              <div className="text-sm text-zinc-400">{selectedFrame.description}</div>
            </div>
            <button 
              onClick={() => { setSelectedFrame(null); setStep(1); }}
              className="ml-auto p-1 text-zinc-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Border width slider */}
      <div className="space-y-3">
        <Label className="text-white flex items-center gap-2">
          <Sliders className="w-4 h-4" />
          Border Width: {borderWidth}px
        </Label>
        <Slider
          value={[borderWidth]}
          onValueChange={(val) => setBorderWidth(val[0])}
          min={10}
          max={100}
          step={5}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-zinc-500">
          <span>Thin (10px)</span>
          <span>Thick (100px)</span>
        </div>
      </div>

      {/* Custom color override - only for template frames */}
      {!useAIFrame && (
        <div className="space-y-3">
          <Label className="text-white flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Custom Color (Optional)
          </Label>
          <div className="flex gap-2">
            <Input
              type="color"
              value={customColor || selectedFrame?.border_color || '#000000'}
              onChange={(e) => setCustomColor(e.target.value)}
              className="w-16 h-10 p-1 bg-zinc-800 border-zinc-700 cursor-pointer"
            />
            <Input
              type="text"
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              placeholder={selectedFrame?.border_color || "Use template color"}
              className="flex-1 bg-zinc-800 border-zinc-700 text-white"
            />
            {customColor && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setCustomColor('')}
                className="text-zinc-400"
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="space-y-2">
        <Label className="text-white">Preview</Label>
        <div 
          className="relative aspect-video rounded-lg overflow-hidden"
          style={{ 
            padding: `${Math.min(borderWidth / 3, 20)}px`,
            background: useAIFrame 
              ? 'linear-gradient(135deg, #06b6d4, #8b5cf6)' 
              : (customColor || selectedFrame?.border_color || '#000')
          }}
        >
          <video 
            src={videoUrl} 
            className="w-full h-full object-cover rounded"
            muted
          />
          {useAIFrame && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="text-xs text-white/70 bg-black/50 px-2 py-1 rounded">
                AI will generate frame based on your prompt
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Apply button */}
      <Button
        onClick={handleApplyFrame}
        disabled={loading || (!selectedFrame && !useAIFrame) || (useAIFrame && !aiPrompt.trim())}
        className={`w-full ${useAIFrame 
          ? 'bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700' 
          : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
        }`}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {useAIFrame ? 'Generating AI Frame...' : 'Applying...'}
          </>
        ) : (
          <>
            {useAIFrame ? <Sparkles className="w-4 h-4 mr-2" /> : <Frame className="w-4 h-4 mr-2" />}
            {useAIFrame ? 'Generate & Apply AI Frame' : 'Apply Frame'}
          </>
        )}
      </Button>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="space-y-6 text-center py-8">
      <div className="relative w-24 h-24 mx-auto">
        <div 
          className="absolute inset-0 rounded-full animate-pulse opacity-30"
          style={{ background: useAIFrame ? 'linear-gradient(135deg, #06b6d4, #8b5cf6)' : (selectedFrame?.border_color || '#9333ea') }}
        />
        <div className="absolute inset-2 rounded-full bg-zinc-900 flex items-center justify-center">
          {currentJob?.status === 'completed' ? (
            <Check className="w-10 h-10 text-green-500" />
          ) : currentJob?.status === 'failed' ? (
            <AlertCircle className="w-10 h-10 text-red-500" />
          ) : useAIFrame ? (
            <Sparkles className="w-10 h-10 text-cyan-400 animate-pulse" />
          ) : (
            <Frame className="w-10 h-10 text-purple-400 animate-pulse" />
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-white mb-2">
          {currentJob?.status === 'completed' 
            ? 'Frame Applied!' 
            : currentJob?.status === 'failed'
            ? 'Failed to Apply Frame'
            : 'Applying Frame'
          }
        </h3>
        <p className="text-sm text-zinc-400">
          {currentJob?.progress_message || 'Processing your video...'}
        </p>
      </div>

      {currentJob && currentJob.status !== 'failed' && currentJob.status !== 'completed' && (
        <div className="space-y-2 max-w-xs mx-auto">
          <Progress value={currentJob.progress || 0} className="h-2" />
          <div className="flex justify-between text-xs text-zinc-500">
            <span>{currentJob.progress || 0}%</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Processing
            </span>
          </div>
        </div>
      )}

      {currentJob?.status === 'completed' && currentJob.output_video_url && (
        <div className="space-y-4">
          <video 
            src={currentJob.output_video_url} 
            controls 
            className="w-full max-w-md mx-auto rounded-lg"
          />
          <div className="flex gap-2 justify-center">
            <Button 
              variant="outline" 
              onClick={() => window.open(currentJob.output_video_url, '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            <Button onClick={onClose}>
              <Check className="w-4 h-4 mr-2" />
              Done
            </Button>
          </div>
        </div>
      )}

      {currentJob?.status === 'failed' && (
        <Button variant="outline" onClick={() => setStep(2)}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Frame className="w-5 h-5 text-purple-400" />
            Video Frame Designer
          </h2>
          <p className="text-sm text-zinc-400">Add decorative frames & borders</p>
        </div>
        {step > 1 && step < 3 && (
          <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
            ← Back to Frames
          </Button>
        )}
      </div>

      {/* Video preview (only in step 1) */}
      {videoUrl && step === 1 && (
        <div className="relative aspect-video bg-zinc-800 rounded-lg overflow-hidden max-h-48">
          <video src={videoUrl} className="w-full h-full object-contain" controls />
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
            {videoName || 'Selected Video'}
          </div>
        </div>
      )}

      {/* Step content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {step === 1 && renderFrameGrid()}
          {step === 2 && renderCustomizeStep()}
          {step === 3 && renderProcessingStep()}
        </motion.div>
      </AnimatePresence>

      {/* Next button for step 1 */}
      {step === 1 && selectedFrame && (
        <Button
          onClick={() => setStep(2)}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          Customize {selectedFrame.name}
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      )}
    </div>
  );
}
